import { Button } from '@odekoteam/doppio';
import { CellContext } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import { Box } from 'theme-ui';

import IconEdit from '@/assets/images/IconEdit.svg?icon';
import { LoadingBox } from '@/components/Collection/LoadingBox';
import {
  formatComponentType,
  formatDateRange,
  formatMarkets,
} from '@/components/Collection/utils';
import DataTable, { DataTableColumn } from '@/components/DataTable';
import {
  GetPastHomepageCollectionsQuery,
  useGetPastHomepageCollectionsQuery,
} from '@/graphql/types/generated';

type PastHomepageCollectionEntry =
  GetPastHomepageCollectionsQuery['pastHomepageCollections'][0];

function EditCell({ row }: CellContext<PastHomepageCollectionEntry, unknown>) {
  const navigate = useNavigate();
  return (
    <Button
      size="small"
      variant="ghost"
      iconLeft={<IconEdit />}
      onPress={() =>
        navigate(
          `/collections/add-to-homepage?collectionId=${row.original.collection.id}&componentType=${row.original.componentType}`,
        )
      }
    />
  );
}

export default function PastHomepageCollectionsTab({
  totalWarehouses,
}: {
  totalWarehouses: number;
}) {
  const [{ data, fetching }] = useGetPastHomepageCollectionsQuery({
    requestPolicy: 'cache-and-network',
    variables: { date: new Date().toISOString().split('T')[0] },
  });

  if (fetching) return <LoadingBox />;

  const pastHomepageColumns: DataTableColumn<PastHomepageCollectionEntry>[] = [
    {
      header: 'Collection Name',
      id: 'name',
      accessorFn: (row) => row.collection.name ?? '(unnamed)',
    },
    {
      header: 'Markets',
      id: 'markets',
      accessorFn: (row) => formatMarkets(row.warehouses, totalWarehouses),
    },
    {
      header: 'Component',
      id: 'component',
      accessorFn: (row) => formatComponentType(row.componentType),
    },
    {
      header: 'Active Dates',
      id: 'activeDates',
      accessorFn: (row) => formatDateRange(row.startDate, row.endDate),
    },
    {
      meta: { width: 80 },
      id: 'edit',
      header: '',
      cell: EditCell,
    },
  ];

  return (
    <Box sx={{ pt: 3 }}>
      <DataTable<PastHomepageCollectionEntry>
        columns={pastHomepageColumns}
        containerStyles={{ marginTop: 0 }}
        data={data?.pastHomepageCollections ?? []}
      />
    </Box>
  );
}
